/**
 * WhatsAppLiveAlerts — R51 + R64.2 · Global new-inbound toaster.
 *
 * Mounted once inside AppShell. Polls `/wa/threads` every 15 seconds
 * while the tab is visible and shows a floating sonner toast for any
 * NEW inbound message the current user hasn't seen. Toasts auto-
 * dismiss after 12s and include a direct "Open" jump to WA Inbox.
 *
 * R64.2 fixes (Acme production loop):
 *   1. Effect no longer depends on `loc.pathname` — the reactive nav
 *      dependency was re-running the effect on EVERY route change,
 *      which fired `tick()` immediately and could double-toast.
 *      Route info is now read via a ref inside `tick`.
 *   2. Seen keys persist to `sessionStorage` (per workspace) so
 *      navigation NEVER causes a re-toast of an already-seen inbound.
 *      Bounded to the most-recent 500 keys to prevent bloat.
 *   3. Only fires the toast when the thread has `unread_count > 0`.
 *      Prior version fired for any thread whose last_direction=in
 *      even if it was already read (Acme regression — read-receipts
 *      changed the group's `last_at`, invalidating the old
 *      `contact_ref|last_at` cache key and re-toasting).
 *   4. Always dispatches `acq:wa-unread-refresh` after a poll so the
 *      header badge decrements without waiting for its own 10s tick.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const POLL_MS = 30_000; // Feb 2026 R281 · Bumped 15s → 30s.
const SEEN_CAP = 500;

export default function WhatsAppLiveAlerts() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  // Track the current pathname via a ref so `tick()` reads the LATEST
  // value without being a reactive dependency of the polling effect.
  const pathRef = useRef(loc.pathname);
  useEffect(() => { pathRef.current = loc.pathname; }, [loc.pathname]);

  useEffect(() => {
    if (!activeWorkspaceId) return undefined;
    const STORAGE_KEY = `acq:wa-alerts-seen:${activeWorkspaceId}`;

    // Hydrate the seen-set from sessionStorage so route changes and
    // hot-reloads don't reset it (root cause of the Acme loop).
    let seenKeys;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      seenKeys = new Set(raw ? JSON.parse(raw) : []);
    } catch {
      seenKeys = new Set();
    }
    // First real load prompt suppression — on the very first tick of a
    // brand-new browser session we swallow toasts and just seed the
    // cache, so returning users don't get 20 pop-ups at once.
    let firstLoad = seenKeys.size === 0;

    const persist = () => {
      try {
        if (seenKeys.size > SEEN_CAP) {
          const arr = Array.from(seenKeys).slice(-SEEN_CAP);
          seenKeys = new Set(arr);
        }
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seenKeys)));
      } catch { /* quota etc — ignore */ }
    };

    let cancelled = false;
    // Iter · Feb 2026 — Latch on the first 403 so users without WA
    // permission stop hitting /wa/threads every 15s (was flooding
    // the console + burning rate-limit budget).
    let forbidden = false;
    let iv = null;
    const tick = async () => {
      if (cancelled || forbidden || document.visibilityState !== "visible") return;
      let rows = [];
      try {
        const { data } = await api.get(
          `/workspaces/${activeWorkspaceId}/wa/threads`,
          { params: { limit: 100 } },
        );
        rows = data?.rows || [];
      } catch (e) {
        if (e?.response?.status === 403) {
          forbidden = true;
          if (iv) { clearInterval(iv); iv = null; }
        }
        return; /* background alerts shouldn't spam errors */
      }

      const isOnWaPage = pathRef.current.startsWith("/app/wa");
      let anyNew = false;

      for (const t of rows) {
        // Guard 1 — only care about inbound messages.
        if (t.last_direction !== "in" || !t.last_at) continue;
        // Guard 2 — ignore threads that are already read.
        //           This was THE bug: read-receipts changed `last_at`
        //           so the composite key kept changing, re-firing the
        //           toast for the same already-read message.
        if (!t.unread_count || Number(t.unread_count) <= 0) continue;

        const key = `${t.contact_ref}|${t.last_at}`;
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        anyNew = true;

        // Suppress toast on the WA page (it renders its own inline
        // toaster to avoid duplication) and during the first-ever
        // browser-session poll.
        if (firstLoad || isOnWaPage) continue;

        const preview = (t.last_text || "").slice(0, 120)
                                || "(non-text message)";
        const who = t.name || t.phone || "New contact";
        toast(`💬 ${who}`, {
          description: preview,
          duration: 12000,
          closeButton: true,
          action: {
            label: "Open",
            onClick: () => navigate(
              `/app/wa-inbox?ref=${encodeURIComponent(t.contact_ref)}`),
          },
          id: `wa-inbound-${key}`, // dedupe if the same key ever recurs
        });
      }

      if (anyNew) {
        persist();
        window.dispatchEvent(new CustomEvent("acq:wa-unread-refresh"));
      }
      firstLoad = false;
    };

    // If the seen-set was newly-empty we still fire tick() to seed it,
    // suppressing toasts inside the loop above.
    tick();
    iv = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);

    // When a mark-read fires elsewhere, clear the corresponding thread's
    // key from the seen set — so if a NEW message arrives on that
    // thread later, we DO toast it (correctness for repeat-conversation
    // scenarios).
    const onMarkRead = (ev) => {
      const ref = ev?.detail?.contact_ref;
      if (!ref) return;
      let touched = false;
      for (const k of seenKeys) {
        if (k.startsWith(`${ref}|`)) { seenKeys.delete(k); touched = true; }
      }
      if (touched) persist();
    };
    window.addEventListener("acq:wa-thread-read", onMarkRead);

    return () => {
      cancelled = true;
      if (iv) clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("acq:wa-thread-read", onMarkRead);
    };
    // Intentionally NOT depending on `loc.pathname` — that was
    // triggering a full effect reset on every route change.
  }, [activeWorkspaceId, navigate]);

  return null;
}
